import React, { useState, useEffect } from 'react';
import { usePublicWebSocket } from '../../hooks/usePublicWebSocket';


/**
 * Event Popup Layout
 * 
 * Horizontal bar that appears when events occur
 * Auto-dismisses after 5 seconds
 * Designed for OBS/vMix overlay
 * 
 * @param {Object} wsData - WebSocket data from parent (required when used in AllLayersLayout)
 * @param {number} matchId - Match ID (used when rendering standalone)
 */
export const EventPopupLayout = ({ wsData, matchId }) => {
  // Use wsData from props if provided, otherwise call hook (for standalone use)
  // CRITICAL: Only call hook when wsData is NOT provided to prevent duplicate listeners
  const hookData = wsData ? { connected: false, events: [] } : usePublicWebSocket(matchId);
  const { connected, events } = wsData || hookData;
  const [activeEvent, setActiveEvent] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const processedIdsRef = React.useRef(new Set());
  const loadTimestampRef = React.useRef(Date.now());
  const queueRef = React.useRef([]);
  const showingRef = React.useRef(false);
  const timerRef = React.useRef(null);

  // Show next event from queue
  const showNext = React.useCallback(() => {
    if (queueRef.current.length === 0) {
      showingRef.current = false;
      return;
    }
    showingRef.current = true;
    const nextEvent = queueRef.current.shift();
    console.log('[EventPopup] Showing event:', nextEvent.id, nextEvent.event_type);
    setActiveEvent(nextEvent);
    setIsVisible(true);

    timerRef.current = setTimeout(() => {
      setIsVisible(false);
      // Small gap between popups
      setTimeout(() => {
        setActiveEvent(null);
        showNext();
      }, 500);
    }, 5000);
  }, []);

  useEffect(() => {
    console.log('[EventPopup] useEffect triggered, events.length:', events.length);
    if (events.length === 0) return;

    // Find ALL new events we haven't processed yet
    const newEvents = events.filter(e => {
      if (processedIdsRef.current.has(e.id)) {
        console.log('[EventPopup] Event', e.id, 'already processed');
        return false;
      }
      const ts = new Date(e.created_at).getTime();
      if (ts < loadTimestampRef.current) {
        console.log('[EventPopup] Event', e.id, 'too old (ts:', ts, 'vs', loadTimestampRef.current, ')');
        return false;
      }
      return true;
    });

    console.log('[EventPopup] Found', newEvents.length, 'new events to process');
    if (newEvents.length === 0) return;

    // events is newest-first; reverse to queue in chronological order (oldest first)
    const chronological = [...newEvents].reverse();

    // Skip yellow_card popups when a second_yellow_red_card exists for the same player
    // (the red card banner already conveys "2nd yellow → red card")
    const filtered = chronological.filter(e => {
      if (e.event_type === 'yellow_card') {
        const playerId = e.player_id || e.player?.id;
        const hasAutoRed = events.some(other =>
          other.event_type === 'second_yellow_red_card' &&
          (other.player_id === playerId || other.player?.id === playerId)
        );
        if (hasAutoRed) return false;
      }
      return true;
    });

    // Mark all as processed (even filtered ones, so they don't re-appear)
    chronological.forEach(e => processedIdsRef.current.add(e.id));
    filtered.forEach(e => {
      queueRef.current.push(e);
    });

    // If not currently showing, start
    if (!showingRef.current) {
      showNext();
    }
  }, [events, showNext]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  if (!connected || !isVisible || !activeEvent) {
    return null;
  }

  // Get event styling
  const getEventIcon = (type) => {
    const eventType = type?.toLowerCase() || '';
    if (eventType.includes('goal')) return '⚽';
    if (eventType.includes('second_yellow')) return '🟨🟥';
    if (eventType.includes('red')) return '🟥';
    if (eventType.includes('yellow')) return '🟨';
    if (eventType.includes('sub')) return '🔄';
    return '📌';
  };

  const getEventColor = (type) => {
    const eventType = type?.toLowerCase() || '';
    if (eventType.includes('goal')) return 'from-green-600 to-green-500';
    if (eventType.includes('second_yellow') || eventType.includes('red')) return 'from-red-600 to-red-500';
    if (eventType.includes('yellow')) return 'from-yellow-600 to-yellow-500';
    if (eventType.includes('sub')) return 'from-blue-600 to-blue-500';
    return 'from-gray-600 to-gray-500';
  };

  const eventType = activeEvent.event_type;
  const playerName = activeEvent.player
    ? `${activeEvent.player.first_name} ${activeEvent.player.last_name}`
    : 'Unknown Player';

  const formatEventType = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('second_yellow')) return 'SECOND YELLOW / RED CARD';
    return (type || 'Event').toUpperCase().replace(/_/g, ' ');
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 flex justify-center p-6">
      {/* Animated popup bar */}
      <div 
        className={`
          bg-gradient-to-r ${getEventColor(eventType)} 
          text-white px-8 py-4 rounded-lg shadow-2xl
          transform transition-all duration-500 ease-out
          ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}
          max-w-4xl w-full
        `}
      >
        <div className="flex items-center justify-between">
          {/* Event icon and type */}
          <div className="flex items-center gap-4">
            <span className="text-6xl drop-shadow-2xl">{getEventIcon(eventType)}</span>
            <div>
              {/* Player Name - LARGER */}
              <div className="text-4xl font-black uppercase tracking-wide drop-shadow-lg" style={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.8), -1px -1px 0 rgba(0,0,0,0.5), 1px -1px 0 rgba(0,0,0,0.5), -1px 1px 0 rgba(0,0,0,0.5), 1px 1px 0 rgba(0,0,0,0.5)'
              }}>
                {playerName}
              </div>
              {/* Event Type - Below player name, slightly smaller */}
              <div className="text-2xl font-bold opacity-95 drop-shadow-md" style={{
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
              }}>
                {formatEventType(eventType)}
              </div>
            </div>
          </div>

          {/* Minute */}
          <div className="text-right">
            <div className="text-5xl font-black drop-shadow-xl" style={{
              textShadow: '2px 2px 4px rgba(0,0,0,0.9)'
            }}>
              {activeEvent.minute}'
            </div>
            <div className="text-base font-semibold opacity-90">
              {activeEvent.half === 1 ? '1ST HALF' : '2ND HALF'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventPopupLayout;
